import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const ROLES = ["entrepreneur", "advisor", "admin", "provider", "investor", "accelerator"];

export default function AdminUsersBlock() {
  const { user: me } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {id, email, name, role, company, lang, password}
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async (query = "") => {
    setErr("");
    try {
      const r = await api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setUsers(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  if (!me || me.role !== "admin") return null;

  const startEdit = (u) => setEditing({
    id: u.id,
    email: u.email,
    name: u.name,
    role: u.role,
    company: u.company || "",
    lang: u.lang || "en",
    password: "",
  });

  const saveEdit = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        name: editing.name,
        email: editing.email,
        role: editing.role,
        company: editing.company,
        lang: editing.lang,
      };
      if (editing.password) body.password = editing.password;
      await api.put(`/admin/users/${editing.id}`, body);
      setEditing(null);
      await load(q);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doDelete = async (uid) => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/users/${uid}`);
      setConfirmDel(null);
      await load(q);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const toggleJournal = async (u) => {
    setErr("");
    try {
      const next = !(u.features?.journal_enabled);
      await api.patch(`/admin/users/${u.id}/features`, { journal_enabled: next });
      await load(q);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="admin-users-block">
      <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{t("admin.title")}</h3>
      <p className="tb-muted" style={{ marginTop: 0 }}>{t("admin.subtitle")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 10 }} data-testid="admin-error">{err}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(q); }}
          placeholder={t("admin.searchPlaceholder")}
          data-testid="admin-search"
          style={{ flex: 1 }}
        />
        <button type="button" className="tb-ghost-btn" onClick={() => load(q)} data-testid="admin-search-btn">
          {t("admin.search")}
        </button>
      </div>

      {users === null ? (
        <div className="tb-loading">{t("common.loading")}</div>
      ) : users.length === 0 ? (
        <p className="tb-muted">{t("admin.noResults")}</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="tb-admin-table" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>{t("auth.name")}</th>
                <th>{t("auth.email")}</th>
                <th>{t("auth.role")}</th>
                <th>{t("auth.company")}</th>
                <th style={{ textAlign: "center" }}>Journal / Brain</th>
                <th style={{ textAlign: "right" }}>{t("admin.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-row-${u.id}`}>
                  <td><strong>{u.name || "—"}</strong></td>
                  <td>{u.email}</td>
                  <td><span className={`tb-badge ${u.role === "admin" ? "good" : "muted"}`}>{u.role}</span></td>
                  <td>{u.company || "—"}</td>
                  <td style={{ textAlign: "center" }}>
                    {u.role === "admin" ? (
                      <span className="tb-muted" style={{ fontSize: "0.78rem" }}>always on</span>
                    ) : (
                      <label className="tb-toggle" data-testid={`admin-toggle-journal-${u.id}`}>
                        <input
                          type="checkbox"
                          checked={!!u.features?.journal_enabled}
                          onChange={() => toggleJournal(u)}
                          data-testid={`admin-toggle-journal-input-${u.id}`}
                        />
                        <span>{u.features?.journal_enabled ? "Enabled" : "Disabled"}</span>
                      </label>
                    )}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className="tb-ghost-btn" onClick={() => startEdit(u)} data-testid={`admin-edit-${u.id}`} style={{ padding: "6px 12px", fontSize: "0.82rem" }}>
                      {t("admin.edit")}
                    </button>
                    {u.id !== me.id && (
                      <button type="button" className="tb-danger-btn" onClick={() => setConfirmDel(u)} data-testid={`admin-delete-${u.id}`} style={{ marginLeft: 6 }}>
                        {t("admin.delete")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="tb-block" style={{ marginTop: 14 }} data-testid="admin-edit-form">
          <h4 style={{ marginTop: 0 }}>{t("admin.editTitle")} — {editing.email}</h4>
          <div className="tb-form">
            <div><label>{t("auth.name")}</label><input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-edit-name" /></div>
            <div><label>{t("auth.email")}</label><input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="admin-edit-email" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label>{t("auth.role")}</label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="admin-edit-role">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label>{t("auth.lang")}</label>
                <select value={editing.lang} onChange={(e) => setEditing({ ...editing, lang: e.target.value })} data-testid="admin-edit-lang">
                  <option value="en">English</option><option value="fr">Français</option>
                </select>
              </div>
            </div>
            <div><label>{t("auth.company")}</label><input type="text" value={editing.company} onChange={(e) => setEditing({ ...editing, company: e.target.value })} data-testid="admin-edit-company" /></div>
            <div>
              <label>{t("admin.resetPassword")}</label>
              <input type="text" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder={t("admin.resetPasswordHint")} data-testid="admin-edit-password" minLength={6} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="tb-primary-btn" onClick={saveEdit} disabled={busy} data-testid="admin-edit-save">{busy ? t("auth.loading") : t("admin.save")}</button>
              <button type="button" className="tb-ghost-btn" onClick={() => setEditing(null)}>{t("book.cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="tb-block" style={{ marginTop: 14, borderColor: "#5e2a2a" }} data-testid="admin-confirm-delete">
          <h4 style={{ marginTop: 0, color: "#ff8f8f" }}>{t("admin.confirmDeleteTitle")}</h4>
          <p>{t("admin.confirmDeleteBody")} <strong>{confirmDel.email}</strong></p>
          <p className="tb-muted" style={{ fontSize: "0.82rem" }}>{t("admin.confirmDeleteHint")}</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="tb-danger-btn" onClick={() => doDelete(confirmDel.id)} disabled={busy} data-testid="admin-confirm-delete-btn">
              {busy ? t("auth.loading") : t("admin.confirmDeleteCta")}
            </button>
            <button type="button" className="tb-ghost-btn" onClick={() => setConfirmDel(null)}>{t("book.cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
